<template>
  <div
    class="outreach-contact-articles"
    data-testid="outreach-contact-recent-articles-mentions"
  >
    <SkeletonMentionStream v-if="loading && !mentions.length" />

    <div
      v-else-if="!mentions.length && !hideNoResultsMessage"
      class="q-pt-md"
    >
      {{
        getLocaleText("outreach_contact_recent_articles_mentions.no_results")
      }}
    </div>

    <QInfiniteScroll
      v-else
      ref="infiniteScroll"
      :offset="20"
      :scroll-target="scrollTarget"
      @load="loadMore"
    >
      <template #loading>
        <SkeletonMentionStream />
      </template>

      <div
        v-for="mention in mentions"
        :key="mention.id"
        class="outreach-contact-articles__mention"
      >
        <div>
          <BaseLineClamp
            v-slot="{ clamped }"
            :lines="1"
          >
            <div
              class="outreach-contact-articles__mention-title outreach-contact-articles__mention-link"
              @click="clickMention(mention)"
            >
              {{ mention.title }}

              <BaseStreemTooltip v-if="clamped">
                {{ mention.title }}
              </BaseStreemTooltip>
            </div>
          </BaseLineClamp>

          <div class="outreach-contact-articles__mention-info">
            <TimeAgo
              :date="mention.timestamp"
              :timezone="mention.source.time_zone"
            />
          </div>

          <RouterLink
            v-if="mention.source.outlet_id"
            class="outreach-contact-articles__mention-source"
            :to="{
              name: 'outreach-search-outlet',
              params: { id: mention.source.outlet_id },
            }"
          >
            {{ mention.source.name }}
          </RouterLink>

          <div
            v-else
            class="outreach-contact-articles__mention-info"
          >
            {{ mention.source.name }}
          </div>
        </div>

        <ActionSheet
          v-if="canClickMention(mention)"
          :actions="[
            'email-or-share',
            'push-to-team',
            'tag-media-item',
            'add-to-organisation-report',
            'view-internal-link',
            'show-debug-info',
          ]"
          :mention="mention"
          :mentions="mentions"
          :stream="stream"
        />
      </div>
    </QInfiniteScroll>
  </div>
</template>

<script setup>
import { debounce } from "lodash-es";
import { computed, getCurrentInstance, onUnmounted, ref, watch } from "vue";

import { getLocaleText } from "shared/boot/i18n";
import { BaseLineClamp, BaseStreemTooltip } from "shared/components/base";
import TimeAgo from "shared/components/core/various/TimeAgo.vue";
import SkeletonMentionStream from "shared/components/SkeletonMentionStream.vue";
import useMention from "shared/composables/useMention";
import useRequestMentions from "shared/composables/useRequestMentions";
import DateRange from "shared/helpers/DateRange";
import { getSources } from "shared/helpers/media";
import StreamFilters from "shared/helpers/StreamFilters";
import features from "shared/services/features";

import ActionSheet from "src/components/ActionSheet";

const props = defineProps({
  stream: {
    type: Object,
    required: true,
  },
  fetchingService: {
    type: Object,
    required: true,
  },
  defaultRange: {
    type: DateRange,
    default: () => DateRange.lastThirtyDays(),
  },
  hideNoResultsMessage: {
    type: Boolean,
  },
  scrollTarget: {
    type: String,
    default: "",
  },
  streamFilters: {
    type: StreamFilters,
    default: null,
  },
});

const infiniteScroll = ref(null);

const { mentionClicked } = useMention(props, getCurrentInstance());

const { loading, mentions, setFilters, loadMentions, hasReachedEndOfMentions } =
  useRequestMentions(props);

const debounceLoadMentions = debounce(loadMentions, 500);

const mentionOptions = computed(() => ({
  ...(props.stream.influencer_id
    ? {
        endpoint: `influencers/${props.stream.influencer_id}/recent_articles`,
        skipMediaValidation: true,
        keyword: props.stream.keyword,
      }
    : {}),
}));

watch(
  () => props.streamFilters,
  () => {
    if (props.streamFilters) {
      setFilters(props.streamFilters.requestFilters());
    }

    debounceLoadMentions(mentionOptions.value);
  },
  { deep: true, immediate: true }
);

watch(
  () => props.stream,
  () => {
    debounceLoadMentions(mentionOptions.value);
  }
);

async function loadMore(_index, done) {
  if (
    hasReachedEndOfMentions.value ||
    (infiniteScroll.value && !mentions.value.length)
  ) {
    infiniteScroll.value.stop();

    return done();
  }

  if (loading.value) {
    return done();
  }

  await loadMentions({
    ...mentionOptions.value,
    range: props.defaultRange,
    keepAlreadyLoadedMentions: Boolean(mentions.value.length),
  });

  return done();
}

function hasSourceFeatureEnabled(mention) {
  const source = getSources().find(({ medium }) => medium === mention.medium);

  if (!source) return false;

  return features.has(source.featureFlag);
}

function canClickMention(mention) {
  if (mention.public_access) return true;

  return hasSourceFeatureEnabled(mention);
}

function clickMention(mention) {
  if (!canClickMention(mention)) return;

  mentionClicked(mention);
}

onUnmounted(() => {
  debounceLoadMentions.cancel();
});
</script>

<style lang="scss" scoped>
.outreach-contact-articles {
  display: flex;
  flex-direction: column;

  &__mention {
    position: relative;
    display: flex;
    align-items: start;
    gap: 1rem;
    justify-content: space-between;

    &:first-child {
      padding-bottom: 20px;
    }

    &:not(:first-child) {
      border-top: 1px solid var(--s-color-denim-2);
      padding-block: 20px;
    }

    &-actions {
      position: absolute;
      top: 0;
      right: 0;
    }

    &-link {
      cursor: pointer;
      color: var(--s-color-blue-9);

      &:hover {
        color: var(--s-color-blue-5);
      }
    }

    &-title {
      position: relative;
      font-size: var(--s-font-size-lg);
      line-height: 150%;
      font-weight: 700;
    }

    &-info {
      margin-top: 8px;
      font-size: var(--s-font-size-md);
      font-weight: 400;
      line-height: 20px;
      color: var(--s-color-denim-7);
    }

    &-source {
      cursor: pointer;
      margin-top: 4px;
      line-height: 150%;
      color: var(--s-color-blue-9);

      &:hover {
        color: var(--s-color-blue-5);
      }
    }
  }
}
</style>
